package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.reactive.EffectBody
import kotlin.jvm.JvmStatic

/**
 * 单选按钮。
 *
 * 本身只是「选中/未选中」的视觉单元；互斥行为由 [RadioGroup] 接线驱动：RadioGroup 在
 * `child(UIElement)` 时给本元素注入 [onSelectRequest]，点击时调用该回调通知父 group 切换 selectedValue。
 */
class RadioButton private constructor() : UIElement<RadioButton>() {

    private var groupValueText: String = ""
    private var selectedFlag: Boolean = false

    // 由 RadioGroup 注入：本控件被点击时通知 group 切换 selectedValue
    private var onSelectRequestCallback: EffectBody? = null

    // === 视觉覆盖字段（可空，null = 回退主题 token） ===
    private var dotColorValue: Int? = null
    private var focusBorderColorValue: Int? = null
    private var boxBackgroundColorValue: Int? = null
    private var boxBorderColorValue: Int? = null
    private var dotInsetValue: Float? = null
    private var disabledOpacityValue: Float? = null
    private var hoverOverlayColorValue: Int? = null
    private var hoverOverlayOpacityValue: Float? = null
    private var pressedOverlayColorValue: Int? = null
    private var pressedOverlayOpacityValue: Float? = null
    private var focusOverlayColorValue: Int? = null
    private var focusOverlayOpacityValue: Float? = null

    init {
        focusableField = true
        // 默认 onClick：通知 group 选中自身
        super.onClick(UiEventHandler { requestSelect() })
    }

    override fun onClick(handler: UiEventHandler<MouseEvent>?): RadioButton {
        // 包装：先通知 group，再调用外部 handler，保证 DSL 里写 onClick = action() 也能切换状态
        super.onClick(
            UiEventHandler { e ->
                requestSelect()
                handler?.accept(e)
            },
        )
        return this
    }

    protected override fun self(): RadioButton = this

    private fun requestSelect() {
        onSelectRequestCallback?.run()
    }

    fun groupValue(v: String?): RadioButton {
        groupValueText = v ?: ""
        return this
    }

    fun groupValue(): String = groupValueText

    fun selected(v: Boolean): RadioButton {
        selectedFlag = v
        return this
    }

    fun selected(): Boolean = selectedFlag

    /** 由 [RadioGroup] 调用注入回调；外部一般无需直接使用。 */
    fun onSelectRequest(r: EffectBody?): RadioButton {
        onSelectRequestCallback = r
        return this
    }

    // === 视觉覆盖 fluent setter / getter ===

    fun dotColor(v: Int?): RadioButton {
        dotColorValue = v
        return this
    }

    fun dotColor(): Int? = dotColorValue

    fun focusBorderColor(v: Int?): RadioButton {
        focusBorderColorValue = v
        return this
    }

    fun focusBorderColor(): Int? = focusBorderColorValue

    fun boxBackgroundColor(v: Int?): RadioButton {
        boxBackgroundColorValue = v
        return this
    }

    fun boxBackgroundColor(): Int? = boxBackgroundColorValue

    fun boxBorderColor(v: Int?): RadioButton {
        boxBorderColorValue = v
        return this
    }

    fun boxBorderColor(): Int? = boxBorderColorValue

    fun dotInset(v: Float?): RadioButton {
        dotInsetValue = v
        return this
    }

    fun dotInset(): Float? = dotInsetValue

    fun disabledOpacity(v: Float?): RadioButton {
        disabledOpacityValue = v
        return this
    }

    fun disabledOpacity(): Float? = disabledOpacityValue

    fun hoverOverlayColor(v: Int?): RadioButton {
        hoverOverlayColorValue = v
        return this
    }

    fun hoverOverlayColor(): Int? = hoverOverlayColorValue

    fun hoverOverlayOpacity(v: Float?): RadioButton {
        hoverOverlayOpacityValue = v
        return this
    }

    fun hoverOverlayOpacity(): Float? = hoverOverlayOpacityValue

    fun pressedOverlayColor(v: Int?): RadioButton {
        pressedOverlayColorValue = v
        return this
    }

    fun pressedOverlayColor(): Int? = pressedOverlayColorValue

    fun pressedOverlayOpacity(v: Float?): RadioButton {
        pressedOverlayOpacityValue = v
        return this
    }

    fun pressedOverlayOpacity(): Float? = pressedOverlayOpacityValue

    fun focusOverlayColor(v: Int?): RadioButton {
        focusOverlayColorValue = v
        return this
    }

    fun focusOverlayColor(): Int? = focusOverlayColorValue

    fun focusOverlayOpacity(v: Float?): RadioButton {
        focusOverlayOpacityValue = v
        return this
    }

    fun focusOverlayOpacity(): Float? = focusOverlayOpacityValue

    companion object {
        /** 创建一个单选按钮。 */
        @JvmStatic
        fun create(): RadioButton = RadioButton()
    }
}
